/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import styled from 'styled-components';

export type AzureMLTabProps = {
    activeStep: AMLConnectionSteps;
    onNext: () => void;
    onDismiss: () => void;
    onPrevious?: () => void;
    isValid?: boolean;
    isFooterDisabled?: boolean;
    isLoading?: boolean;
    isBackDisabled?: boolean;
};

const messages = defineMessages({
    back: {
        id: 'customText.azureMLTabConnectionFooter.back',
        defaultMessage: 'Back'
    },
    next: {
        id: 'customText.azureMLTabConnectionFooter.next',
        defaultMessage: 'Next'
    },
    letGo: {
        id: 'customText.azureMLTabConnectionFooter.letGo',
        defaultMessage: "Let's go"
    },
    createConnection: {
        id: 'customText.azureMLTabConnectionFooter.createConnection',
        defaultMessage: 'Create Connection'
    },
    initializingAmlProject: {
        id: 'customText.azureMLTabConnectionFooter.initializingAmlProject',
        defaultMessage: 'Initializing Project...'
    }
});

const modalTheme = getScopedTheme('AzureMLTabConnectionFooter');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const Wrapper = styled(Stack)({ borderTop: borderStyle, position: 'absolute', bottom: '0px', width: '100%' });
const modalButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { padding: '0', fontSize: '12px', minWidth: '60px' } });

export const AzureMLTabConnectionFooter = (props: AzureMLTabProps) => {
    const {
        activeStep,
        onPrevious,
        onNext,
        onDismiss,
        isLoading = false,
        isBackDisabled = false,
        isFooterDisabled = false,
        isValid = true
    } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const nextBtnText =
        activeStep === AMLConnectionSteps.Review
            ? localize(messages.createConnection)
            : activeStep === AMLConnectionSteps.Overview
            ? localize(messages.letGo)
            : localize(messages.next);

    return (
        <Wrapper horizontal horizontalAlign="space-between" tokens={{ padding: isLargeOrGreater ? '16px 0' : '10px' }}>
            <Stack horizontal tokens={{ childrenGap: 8, padding: `0 0 0 ${isLargeOrGreater ? '32px' : '0'}` }}>
                {(isBackDisabled || onPrevious) && (
                    <DefaultButton
                        disabled={isLoading || isBackDisabled || isFooterDisabled}
                        onClick={onPrevious}
                        styles={!isLargeOrGreater && modalButtonStyles}>
                        {localize(messages.back)}
                    </DefaultButton>
                )}
                <PrimaryButton
                    disabled={isLoading || isFooterDisabled || !isValid}
                    onClick={onNext}
                    styles={!isLargeOrGreater && modalButtonStyles}>
                    {nextBtnText}
                </PrimaryButton>
                {isLoading && <Spinner label={localize(messages.initializingAmlProject)} ariaLive="assertive" labelPosition="right" />}
            </Stack>
            <DefaultButton disabled={isFooterDisabled} onClick={onDismiss} styles={!isLargeOrGreater && modalButtonStyles}>
                {localize(globalMessages.cancel)}
            </DefaultButton>
        </Wrapper>
    );
};
